<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-call-transcripts.WEB.T1..T7 — the call transcript reader, in the
// call detail and (compact) in the conversation bubble's fold-out. It mirrors
// the desktop reader (kaptionai desktop/shell/rail.js renderTranscript,
// kaptionai desktop-shell spec CALL 14) and the extension (RecordingsPanel.tsx, EXT.7):
//  - one row per turn: clock (seeks), speaker in their colour, the words as
//    seek targets; customer in slate, each agent their own colour;
//  - while playing the current turn is "now" and scrolls into view (paused
//    4 s after the user scrolls) and the current word lights (0.35 s hold);
//  - inline markers: "Ana passed the call to Bruno · note", "On hold 0:42",
//    "Recording notice played", consults (not recorded), audio gaps;
//  - header: duration, language, consent badge, copy, download .txt
//    (permission-gated) and find in transcript (call-transcript-search.WEB.5);
//  - every status: recording (live), queued/transcribing, ready, partial,
//    failed (retry for admins), not recorded, removed by retention.
// Kaption style guide: Transcripts are orange (#F97316) with audio-lines.
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useKaptionTranscriptPlayback } from 'dashboard/composables/useKaptionTranscriptPlayback';
import KaptionCallTranscriptTurn from './KaptionCallTranscriptTurn.vue';
import {
  CUSTOMER_TONE,
  MARKER_KIND,
  READABLE_STATUSES,
  TRANSCRIPT_STATUS,
  WORKING_STATUSES,
  displayMarkers,
  findMatches,
  firstName,
  formatClock,
  readingOrder,
  speakerLabel,
  transcriptPlainText,
} from './kaptionCallTranscriptModel';
import {
  PAPER_CHIP,
  PAPER_EYEBROW,
  PAPER_ICON_TILE,
  PAPER_INPUT,
  TRANSCRIPT_ACCENT,
  TRANSCRIPT_ICON,
  TRANSCRIPT_TINT,
} from './kaptionCallPaper';

const props = defineProps({
  call: { type: Object, required: true },
  // Normalised transcript (kaptionCallTranscriptModel.normalizeTranscript).
  transcript: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  loadError: { type: String, default: null },
  // Shared with KaptionCallPlayer; the compact reader makes its own.
  playback: { type: Object, default: null },
  // The call is live and being recorded.
  live: { type: Boolean, default: false },
  canRetry: { type: Boolean, default: false },
  canDownload: { type: Boolean, default: false },
  retrying: { type: Boolean, default: false },
  // call-transcript-search.WEB.3 — the find bar opens pre-filled.
  initialQuery: { type: String, default: '' },
  compact: { type: Boolean, default: false },
  // Height of whatever is pinned above the reader (the detail's player), so
  // the find bar sticks just under it.
  stickyTop: { type: Number, default: 0 },
  // Compact: how many turns/markers to show.
  previewItems: { type: Number, default: 5 },
});

const emit = defineEmits(['retry', 'reload', 'openDetail']);
const { t } = useI18n();

const transcriptRef = computed(() => props.transcript);
const ownPlayback = props.playback
  ? null
  : useKaptionTranscriptPlayback(transcriptRef);
const pb = computed(() => props.playback || ownPlayback);

const status = computed(() => props.transcript?.status || null);
const isReadable = computed(() => READABLE_STATUSES.includes(status.value));
const isWorking = computed(() => WORKING_STATUSES.includes(status.value));

const view = computed(() => {
  if (props.live) return 'recording';
  if (props.loading && !props.transcript) return 'loading';
  if (props.loadError && !props.transcript) return 'error';
  if (isWorking.value) return 'processing';
  if (status.value === TRANSCRIPT_STATUS.FAILED) return 'failed';
  if (status.value === TRANSCRIPT_STATUS.NOT_RECORDED) return 'not_recorded';
  if (status.value === TRANSCRIPT_STATUS.SKIPPED)
    return props.transcript?.error === 'not_recorded'
      ? 'not_recorded'
      : 'skipped';
  if (status.value === TRANSCRIPT_STATUS.PURGED) return 'purged';
  if (isReadable.value)
    return props.transcript.turns.length ? 'ready' : 'empty';
  return 'loading';
});

// ---- speakers -------------------------------------------------------------
const legNames = computed(() => {
  const map = new Map();
  (props.call.legs || []).forEach(leg => {
    if (leg.user?.id) map.set(leg.user.id, leg.user.name);
  });
  if (props.call.answeredBy?.id)
    map.set(props.call.answeredBy.id, props.call.answeredBy.name);
  return map;
});
const speakerByKey = computed(() => {
  const map = new Map();
  (props.transcript?.speakers || []).forEach(s => map.set(s.key, s));
  return map;
});
const nameOfKey = key => {
  const speaker = speakerByKey.value.get(key);
  const fallback = speaker?.userId ? legNames.value.get(speaker.userId) : '';
  return (
    speakerLabel(speaker, {
      customer:
        props.call.contactName || t('KAPTION_CALLS.TRANSCRIPT.CUSTOMER'),
      agent: t('KAPTION_CALLS.TRANSCRIPT.AGENT'),
    }) ||
    fallback ||
    t('KAPTION_CALLS.TRANSCRIPT.AGENT')
  );
};
const nameOfUser = userId => {
  if (!userId) return t('KAPTION_CALLS.TRANSCRIPT.AGENT');
  const speaker = (props.transcript?.speakers || []).find(
    s => s.userId === userId
  );
  return (
    speaker?.name ||
    legNames.value.get(userId) ||
    t('KAPTION_CALLS.TRANSCRIPT.AGENT')
  );
};
const toneOf = key => speakerByKey.value.get(key)?.tone || CUSTOMER_TONE;

// ---- markers --------------------------------------------------------------
const markers = computed(() =>
  props.transcript
    ? displayMarkers(props.transcript.markers, props.transcript.durationSeconds)
    : []
);
const MARKER_ICONS = {
  [MARKER_KIND.HANDOVER]: 'i-lucide-arrow-right-left',
  [MARKER_KIND.HOLD_ON]: 'i-lucide-pause',
  [MARKER_KIND.CONSULT]: 'i-lucide-users',
  [MARKER_KIND.NOTICE]: 'i-lucide-mic text-[#DC2626]',
  [MARKER_KIND.GAP]: 'i-lucide-circle-slash',
  [MARKER_KIND.RECORDING_STOPPED]: 'i-lucide-mic-off',
  [MARKER_KIND.RECORDING_RESUMED]: 'i-lucide-mic',
};
const markerText = marker => {
  switch (marker.kind) {
    case MARKER_KIND.HANDOVER:
      return t('KAPTION_CALLS.TRANSCRIPT.MARKERS.HANDOVER', {
        from: firstName(nameOfUser(marker.fromUserId)),
        to: firstName(nameOfUser(marker.toUserId)),
      });
    case MARKER_KIND.HOLD_ON:
      return marker.durationSeconds
        ? t('KAPTION_CALLS.TRANSCRIPT.MARKERS.HOLD', {
            duration: formatClock(marker.durationSeconds),
          })
        : t('KAPTION_CALLS.TRANSCRIPT.MARKERS.HOLD_OPEN');
    case MARKER_KIND.CONSULT:
      return t('KAPTION_CALLS.TRANSCRIPT.MARKERS.CONSULT', {
        name: firstName(
          nameOfUser(
            marker.toUserId ??
              marker.userIds.find(id => id !== marker.fromUserId) ??
              marker.userIds[marker.userIds.length - 1]
          )
        ),
      });
    case MARKER_KIND.NOTICE:
      return t('KAPTION_CALLS.TRANSCRIPT.MARKERS.NOTICE');
    case MARKER_KIND.GAP:
      return t('KAPTION_CALLS.TRANSCRIPT.MARKERS.GAP');
    case MARKER_KIND.RECORDING_STOPPED:
      return t('KAPTION_CALLS.TRANSCRIPT.MARKERS.RECORDING_STOPPED');
    case MARKER_KIND.RECORDING_RESUMED:
      return t('KAPTION_CALLS.TRANSCRIPT.MARKERS.RECORDING_RESUMED');
    default:
      return '';
  }
};

// ---- rows / reading order -------------------------------------------------
const rows = computed(() => pb.value.rows.value);
const items = computed(() => {
  const all = readingOrder(rows.value, markers.value).filter(
    item => item.type === 'turn' || markerText(item.marker)
  );
  return props.compact ? all.slice(0, props.previewItems) : all;
});
const hiddenCount = computed(() =>
  props.compact
    ? Math.max(0, rows.value.length + markers.value.length - items.value.length)
    : 0
);

// ---- find in transcript (call-transcript-search.WEB.5) --------------------
const findOpen = ref(!!props.initialQuery);
const query = ref(props.initialQuery || '');
const matchIndex = ref(0);
const findInput = ref(null);
const matches = computed(() =>
  findOpen.value ? findMatches(rows.value, query.value) : []
);
const rangesByRow = computed(() => {
  const map = new Map();
  matches.value.forEach((match, i) => {
    if (!map.has(match.rowIndex)) map.set(match.rowIndex, []);
    map
      .get(match.rowIndex)
      .push([match.from, match.to, i === matchIndex.value]);
  });
  return map;
});
const currentMatchRow = computed(
  () => matches.value[matchIndex.value]?.rowIndex ?? -1
);
const revealMatch = () =>
  nextTick(() => {
    if (currentMatchRow.value >= 0)
      pb.value.scrollToRow(currentMatchRow.value, { force: true });
  });
watch(query, () => {
  matchIndex.value = 0;
  revealMatch();
});
watch(
  () => props.initialQuery,
  value => {
    if (!value) return;
    query.value = value;
    findOpen.value = true;
  }
);
const step = delta => {
  const total = matches.value.length;
  if (!total) return;
  matchIndex.value = (matchIndex.value + delta + total) % total;
  revealMatch();
};
const openFind = () => {
  findOpen.value = true;
  nextTick(() => findInput.value?.focus());
};
const closeFind = () => {
  findOpen.value = false;
  query.value = '';
};
const onFindKey = event => {
  if (event.key === 'Enter') {
    event.preventDefault();
    step(event.shiftKey ? -1 : 1);
  } else if (event.key === 'Escape') {
    event.preventDefault();
    closeFind();
  }
};
// Ctrl/Cmd+F while the reader has focus opens our find; outside it the
// browser's find still works.
const onRootKey = event => {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'f') {
    if (props.compact || view.value !== 'ready') return;
    event.preventDefault();
    openFind();
  }
};

// ---- header facts and actions --------------------------------------------
const LANGUAGE_KEYS = { es: 'ES', pt: 'PT', en: 'EN' };
const languageLabel = computed(() => {
  const code = String(props.transcript?.language || '')
    .slice(0, 2)
    .toLowerCase();
  const key = LANGUAGE_KEYS[code];
  return key
    ? t(`KAPTION_CALLS.TRANSCRIPT.LANGUAGES.${key}`)
    : props.transcript?.language || '';
});
const consentLabel = computed(() => {
  const consent = props.transcript?.consent;
  if (!consent) return '';
  if (consent === 'none') return t('KAPTION_CALLS.TRANSCRIPT.CONSENT.NONE');
  if (consent === 'agent') return t('KAPTION_CALLS.TRANSCRIPT.CONSENT.AGENT');
  return t('KAPTION_CALLS.TRANSCRIPT.CONSENT.NOTICE');
});
const isApproximate = computed(() => {
  const tx = props.transcript;
  if (!tx) return false;
  if (tx.wordsCoverage !== null && tx.wordsCoverage < 1) return true;
  return rows.value.some(row => !row.timed || row.tokens.some(tk => tk.approx));
});
const facts = computed(() =>
  [
    props.transcript?.durationSeconds
      ? formatClock(props.transcript.durationSeconds)
      : '',
    languageLabel.value,
  ].filter(Boolean)
);

const plainText = () =>
  transcriptPlainText(props.transcript, {
    title: t('KAPTION_CALLS.TRANSCRIPT.TEXT_TITLE', {
      name: props.call.contactName || t('KAPTION_CALLS.TRANSCRIPT.CUSTOMER'),
    }),
    names: nameOfKey,
    markerText: marker => {
      const text = markerText(marker);
      return marker.note ? `${text} · “${marker.note}”` : text;
    },
  });
const copied = ref(false);
const copy = async () => {
  try {
    await navigator.clipboard.writeText(plainText());
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch (e) {
    // clipboard refused; nothing to undo
  }
};
const download = () => {
  const blob = new Blob([plainText()], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `call-${props.call.id}-transcript.txt`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const statusChip = computed(() => {
  if (props.live)
    return {
      key: 'RECORDING',
      classes: 'bg-[#DC2626]/10 text-[#DC2626]',
      dot: 'bg-[#DC2626] animate-pulse',
    };
  if (!status.value || status.value === TRANSCRIPT_STATUS.READY) return null;
  const tones = {
    pending: [TRANSCRIPT_TINT, TRANSCRIPT_ACCENT],
    processing: [TRANSCRIPT_TINT, TRANSCRIPT_ACCENT],
    partial: ['bg-n-amber-3', 'text-n-amber-11'],
    failed: ['bg-n-ruby-3', 'text-n-ruby-11'],
    skipped: ['bg-n-alpha-2', 'text-n-slate-11'],
    purged: ['bg-n-alpha-2', 'text-n-slate-11'],
    not_recorded: ['bg-n-alpha-2', 'text-n-slate-11'],
  };
  return {
    key: status.value.toUpperCase(),
    classes: (tones[status.value] || tones.skipped).join(' '),
  };
});

// ---- playback hooks -------------------------------------------------------
// The compact reader (bubble) has no audio: a click opens the call detail
// at that moment instead.
const seek = seconds =>
  props.compact ? emit('openDetail', seconds) : pb.value.seek(seconds);
const onUserScroll = () => pb.value.noteUserScroll();
const turnRefs = {};
const setTurnEl = (rowIndex, el) => {
  const node = el?.$el || el;
  turnRefs[rowIndex] = node;
  pb.value.registerTurn(rowIndex, node);
};

defineExpose({ openFind, matches, query });
</script>

<template>
  <div
    data-testid="kaption-call-transcript"
    :data-state="view"
    class="flex flex-col"
  >
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
    <section
      class="flex flex-col gap-3 outline-none"
      tabindex="-1"
      @keydown="onRootKey"
      @wheel.passive="onUserScroll"
      @touchmove.passive="onUserScroll"
    >
      <header v-if="!compact" class="flex flex-col gap-2">
        <div class="flex items-center justify-between gap-2">
          <span class="flex items-center gap-1.5" :class="PAPER_EYEBROW">
            <span
              class="size-3.5"
              :class="[TRANSCRIPT_ICON, TRANSCRIPT_ACCENT]"
            />
            {{ $t('KAPTION_CALLS.TRANSCRIPT.TITLE') }}
            <span
              v-if="statusChip"
              data-testid="kaption-call-transcript-status"
              class="ml-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium normal-case leading-none tracking-normal"
              :class="statusChip.classes"
            >
              <span
                v-if="statusChip.dot"
                class="size-1.5 rounded-full"
                :class="statusChip.dot"
              />
              {{ $t(`KAPTION_CALLS.TRANSCRIPT.STATUS.${statusChip.key}`) }}
            </span>
          </span>
          <div v-if="view === 'ready'" class="flex items-center gap-1">
            <Button
              data-testid="kaption-call-transcript-find"
              icon="i-lucide-search"
              variant="ghost"
              color="slate"
              size="xs"
              :aria-label="$t('KAPTION_CALLS.TRANSCRIPT.FIND')"
              :title="$t('KAPTION_CALLS.TRANSCRIPT.FIND')"
              @click="findOpen ? closeFind() : openFind()"
            />
            <Button
              data-testid="kaption-call-transcript-copy"
              :icon="copied ? 'i-lucide-check' : 'i-lucide-copy'"
              :label="
                copied
                  ? $t('KAPTION_CALLS.TRANSCRIPT.COPIED')
                  : $t('KAPTION_CALLS.TRANSCRIPT.COPY')
              "
              variant="ghost"
              color="slate"
              size="xs"
              @click="copy"
            />
            <Button
              v-if="canDownload"
              data-testid="kaption-call-transcript-download"
              icon="i-lucide-download"
              :label="$t('KAPTION_CALLS.TRANSCRIPT.DOWNLOAD')"
              variant="ghost"
              color="slate"
              size="xs"
              @click="download"
            />
          </div>
        </div>
        <div
          v-if="
            view === 'ready' && (facts.length || consentLabel || isApproximate)
          "
          data-testid="kaption-call-transcript-facts"
          class="flex flex-wrap items-center gap-1.5"
        >
          <span
            v-for="fact in facts"
            :key="fact"
            :class="PAPER_CHIP"
            class="font-mono tabular-nums"
          >
            {{ fact }}
          </span>
          <span
            v-if="consentLabel"
            data-testid="kaption-call-transcript-consent"
            :class="PAPER_CHIP"
          >
            <span class="i-lucide-mic size-3 text-[#DC2626]" />
            {{ consentLabel }}
          </span>
          <span
            v-if="isApproximate"
            data-testid="kaption-call-transcript-approx"
            :class="PAPER_CHIP"
            :title="$t('KAPTION_CALLS.TRANSCRIPT.APPROXIMATE')"
          >
            <span class="i-lucide-timer size-3" />
            {{ $t('KAPTION_CALLS.TRANSCRIPT.APPROX_SHORT') }}
          </span>
        </div>
      </header>

      <!-- Find in transcript — sticks under the player while the turns scroll. -->
      <div
        v-if="findOpen && view === 'ready'"
        data-testid="kaption-call-transcript-findbar"
        class="sticky z-[5] -mx-2 -my-1.5 flex items-center gap-1.5 bg-n-solid-1 px-2 py-1.5"
        :style="{ top: `${stickyTop}px` }"
      >
        <span class="relative flex-1">
          <span
            class="i-lucide-search pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-n-slate-10"
          />
          <input
            ref="findInput"
            v-model="query"
            data-testid="kaption-call-transcript-find-input"
            type="search"
            :placeholder="$t('KAPTION_CALLS.TRANSCRIPT.FIND')"
            :aria-label="$t('KAPTION_CALLS.TRANSCRIPT.FIND')"
            class="w-full !pl-8"
            :class="PAPER_INPUT"
            @keydown="onFindKey"
          />
        </span>
        <span
          data-testid="kaption-call-transcript-find-count"
          class="min-w-[4.5rem] text-center font-mono text-xs tabular-nums text-n-slate-11"
        >
          <template v-if="query.trim()">
            {{
              matches.length
                ? $t('KAPTION_CALLS.TRANSCRIPT.FIND_COUNT', {
                    current: matchIndex + 1,
                    total: matches.length,
                  })
                : $t('KAPTION_CALLS.TRANSCRIPT.FIND_NONE')
            }}
          </template>
        </span>
        <Button
          data-testid="kaption-call-transcript-find-prev"
          icon="i-lucide-chevron-up"
          variant="ghost"
          color="slate"
          size="xs"
          :disabled="!matches.length"
          :aria-label="$t('KAPTION_CALLS.TRANSCRIPT.FIND_PREV')"
          @click="step(-1)"
        />
        <Button
          data-testid="kaption-call-transcript-find-next"
          icon="i-lucide-chevron-down"
          variant="ghost"
          color="slate"
          size="xs"
          :disabled="!matches.length"
          :aria-label="$t('KAPTION_CALLS.TRANSCRIPT.FIND_NEXT')"
          @click="step(1)"
        />
        <Button
          icon="i-lucide-x"
          variant="ghost"
          color="slate"
          size="xs"
          :aria-label="$t('KAPTION_CALLS.TRANSCRIPT.FIND_CLOSE')"
          @click="closeFind"
        />
      </div>

      <!-- Partial: what we have, and a way to try again. -->
      <div
        v-if="status === 'partial' && view === 'ready' && !compact"
        data-testid="kaption-call-transcript-partial"
        class="flex items-center gap-2 rounded-lg border border-n-amber-6 bg-n-amber-2 px-3 py-2 text-xs text-n-amber-11"
      >
        <span class="i-lucide-triangle-alert size-3.5 shrink-0" />
        <span class="flex-1">{{
          $t('KAPTION_CALLS.TRANSCRIPT.PARTIAL_NOTE')
        }}</span>
        <Button
          v-if="canRetry"
          data-testid="kaption-call-transcript-retry"
          :label="$t('KAPTION_CALLS.TRANSCRIPT.RETRY')"
          :is-loading="retrying"
          color="amber"
          variant="faded"
          size="xs"
          @click="emit('retry')"
        />
      </div>

      <!-- Ready: turns and markers in reading order. -->
      <ol
        v-if="view === 'ready'"
        data-testid="kaption-call-transcript-turns"
        class="flex flex-col gap-0.5"
      >
        <template v-for="item in items" :key="item.key">
          <li
            v-if="item.type === 'marker'"
            data-testid="kaption-call-transcript-marker"
            :data-kind="item.marker.kind"
            class="flex items-baseline gap-1.5 py-1.5 pl-[3.875rem] pr-2 text-[11px] text-n-slate-10"
          >
            <span
              class="size-3 shrink-0 self-center"
              :class="MARKER_ICONS[item.marker.kind] || 'i-lucide-dot'"
            />
            <button
              type="button"
              class="shrink-0 font-mono text-[11px] tabular-nums hover:text-n-slate-12"
              @click="seek(item.marker.t)"
            >
              {{ formatClock(item.marker.t) }}
            </button>
            <span class="min-w-0 text-n-slate-11">
              {{ markerText(item.marker) }}
              <span
                v-if="item.marker.note"
                data-testid="kaption-call-transcript-marker-note"
                class="italic"
              >
                {{
                  t('KAPTION_CALLS.TRANSCRIPT.MARKERS.NOTE', {
                    note: item.marker.note,
                  })
                }}
              </span>
            </span>
          </li>
          <KaptionCallTranscriptTurn
            v-else
            :ref="el => setTurnEl(item.rowIndex, el)"
            :row="item.row"
            :speaker-name="nameOfKey(item.row.turn.speaker)"
            :tone="toneOf(item.row.turn.speaker)"
            :is-now="pb.currentRow.value === item.rowIndex"
            :current-word="
              pb.currentRow.value === item.rowIndex ? pb.currentWord.value : -1
            "
            :ranges="rangesByRow.get(item.rowIndex) || []"
            :has-current-match="currentMatchRow === item.rowIndex"
            :compact="compact"
            @seek="seek"
          />
        </template>
      </ol>
      <p
        v-if="view === 'ready' && !compact"
        class="mb-0 pl-[3.875rem] text-[11px] text-n-slate-10"
      >
        {{ $t('KAPTION_CALLS.TRANSCRIPT.WORDS_HINT') }}
      </p>
      <button
        v-if="compact && view === 'ready' && hiddenCount"
        type="button"
        data-testid="kaption-call-transcript-more"
        class="pl-[3.875rem] text-left text-xs text-n-slate-11 hover:text-n-slate-12"
        @click="emit('openDetail', null)"
      >
        {{ t('KAPTION_CALLS.TRANSCRIPT.MORE', { count: hiddenCount }) }}
      </button>

      <!-- Every other state: a quiet tile, a title, one line. -->
      <div
        v-if="view !== 'ready'"
        data-testid="kaption-call-transcript-state"
        class="flex flex-col items-center gap-1.5 px-6 text-center"
        :class="compact ? 'py-4' : 'py-10'"
      >
        <span
          v-if="view === 'loading'"
          class="flex items-center justify-center"
          :class="compact ? 'size-8' : 'size-10'"
        >
          <Spinner class="text-[#F97316]" />
        </span>
        <span v-else :class="[PAPER_ICON_TILE, compact ? 'size-8' : 'size-10']">
          <Spinner
            v-if="view === 'processing'"
            :size="compact ? 16 : 20"
            class="text-[#F97316]"
          />
          <span
            v-else
            :class="[
              {
                recording: 'i-lucide-mic text-[#DC2626] animate-pulse',
                failed: 'i-lucide-circle-alert text-n-ruby-11',
                error: 'i-lucide-circle-alert text-n-ruby-11',
                skipped: 'i-lucide-mic-off text-n-slate-11',
                purged: 'i-lucide-archive-x text-n-slate-11',
                empty: `${TRANSCRIPT_ICON} text-n-slate-11`,
              }[view],
              compact ? 'size-4' : 'size-5',
            ]"
          />
        </span>
        <template v-if="view === 'recording'">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_CALLS.TRANSCRIPT.RECORDING_TITLE') }}
          </p>
          <p class="mb-0 max-w-sm text-xs leading-relaxed text-n-slate-11">
            {{ $t('KAPTION_CALLS.TRANSCRIPT.RECORDING_BODY') }}
          </p>
        </template>
        <template v-else-if="view === 'processing'">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_CALLS.TRANSCRIPT.PROCESSING_TITLE') }}
          </p>
          <p
            v-if="!compact"
            class="mb-0 max-w-sm text-xs leading-relaxed text-n-slate-11"
          >
            {{ $t('KAPTION_CALLS.TRANSCRIPT.PROCESSING_BODY') }}
          </p>
        </template>
        <template v-else-if="view === 'failed'">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_CALLS.TRANSCRIPT.FAILED_TITLE') }}
          </p>
          <p class="mb-0 max-w-sm text-xs leading-relaxed text-n-slate-11">
            {{ $t('KAPTION_CALLS.TRANSCRIPT.FAILED_BODY') }}
          </p>
          <Button
            v-if="canRetry && !compact"
            data-testid="kaption-call-transcript-retry"
            class="mt-2"
            icon="i-lucide-rotate-ccw"
            :label="$t('KAPTION_CALLS.TRANSCRIPT.RETRY')"
            :is-loading="retrying"
            color="slate"
            variant="faded"
            size="sm"
            @click="emit('retry')"
          />
        </template>
        <template v-else-if="view === 'not_recorded'">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_CALLS.TRANSCRIPT.NOT_RECORDED_TITLE') }}
          </p>
          <p class="mb-0 max-w-sm text-xs leading-relaxed text-n-slate-11">
            {{ $t('KAPTION_CALLS.TRANSCRIPT.NOT_RECORDED_BODY') }}
          </p>
        </template>
        <template v-else-if="view === 'skipped'">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_CALLS.TRANSCRIPT.SKIPPED_TITLE') }}
          </p>
          <p class="mb-0 max-w-sm text-xs leading-relaxed text-n-slate-11">
            {{
              transcript?.error === 'notice_not_played'
                ? $t('KAPTION_CALLS.TRANSCRIPT.SKIPPED_BODY_NOTICE')
                : $t('KAPTION_CALLS.TRANSCRIPT.SKIPPED_BODY')
            }}
          </p>
        </template>
        <template v-else-if="view === 'purged'">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_CALLS.TRANSCRIPT.PURGED_TITLE') }}
          </p>
          <p class="mb-0 max-w-sm text-xs leading-relaxed text-n-slate-11">
            {{
              transcript?.retentionDays
                ? $t('KAPTION_CALLS.TRANSCRIPT.PURGED_BODY', {
                    days: transcript.retentionDays,
                  })
                : $t('KAPTION_CALLS.TRANSCRIPT.PURGED_BODY_NO_DAYS')
            }}
          </p>
        </template>
        <template v-else-if="view === 'empty'">
          <p class="mb-0 text-sm text-n-slate-11">
            {{ $t('KAPTION_CALLS.TRANSCRIPT.NO_SPEECH') }}
          </p>
        </template>
        <template v-else-if="view === 'error'">
          <p class="mb-0 text-sm text-n-slate-11">
            {{ $t('KAPTION_CALLS.TRANSCRIPT.LOAD_FAILED') }}
          </p>
          <Button
            class="mt-1"
            :label="$t('KAPTION_CALLS.TRANSCRIPT.RETRY')"
            variant="link"
            size="sm"
            @click="emit('reload')"
          />
        </template>
      </div>
    </section>
  </div>
</template>
